import { useState, forwardRef, useEffect, useRef } from 'react';
import {
  Button,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Slide,
  Box,
  Card,
  Tab,
  Tabs,
  Container,
  Grid,
  Skeleton,
  IconButton,
  Tooltip,
  Chip,
  useMediaQuery,
  Paper,
} from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Iconify from 'components/Iconify';
import useIsMountedRef from 'hooks/useIsMountedRef';
import Scrollbar from 'components/Scrollbar';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { useSnackbar } from 'notistack';
import { capitalCase } from 'change-case';
import { Icon } from '@iconify/react';
import { removeTrailingZeros } from 'utils/formatNumber';
import TransactionOperationModal from './TransactionOperationModal';
// import dayjs from 'dayjs';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

BlockchainTransactionDialog.propTypes = {
  txid: PropTypes.string.isRequired,
  handleClose: PropTypes.func,
};

export default function BlockchainTransactionDialog({ txid, handleClose }) {
  const [data, setData] = useState({});
  const [modal, setModal] = useState(false);
  const [state, setState] = useState({ loading: false, loadComplete: false, error: null });
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTab, setCurrentTab] = useState('basic');

  useEffect(() => {
    getInitData();
    // eslint-disable-next-line
  }, []);

  const handleTabChange = (e, val) => setCurrentTab(val);

  function formatDate(d) {
    if (!d) return '-';
    try {
      // return dayjs(d).format('YYYY-MM-DD HH:mm:ss');
      return new Date(d).toLocaleString(undefined, { hour12: true })
    } catch {
      return d;
    }
  }

  function handleCopy(val, label) {
    navigator.clipboard.writeText(val).then(() => {
      enqueueSnackbar(`${label} copied to clipboard.`);
    });
  }

  // async function retrySwap() {
  //   enqueueSnackbar('Retry swap action triggered!', { variant: 'info' });
  // }

  // async function revalueOrder() {
  //   enqueueSnackbar('Re-value order triggered!', { variant: 'info' });
  // }

  async function getInitData() {
    setState({ ...state, loading: true, loadComplete: false, error: null });
    try {
      const ret = await axiosInstance.get(`/transactions/blockchain/${encodeURIComponent(txid)}`);
      if (isMountedRef.current) {
        setState({ ...state, loading: false, loadComplete: true, error: null });
        setData(ret.data);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error));
      setState({ ...state, loading: false, loadComplete: false, error: getErrorMessage(error) });
    }
  }

  // Create a flat array of label/value/copyIconGroup for gobally-used fields
  const infoGroups = [
    [
      {
        label: 'TxID',
        value: data.txid,
        copy: true,
        monospace: true
      },
      {
        label: 'Address',
        value: data.address,
        copy: true,
        monospace: true
      },
      {
        label: 'Order ID',
        value: data.order_id,
        copy: true,
      },
      {
        label: 'User ID',
        value: data.user_id,
        copy: true,
      }
    ],
    [
      {
        label: 'Blockchain',
        value: capitalCase(data.blockchain ?? ''),
      },
      {
        label: 'Token Type',
        value: capitalCase(data.token_type ?? ''),
      },
      {
        label: 'Coin ID',
        value: data.coin_id,
      },
      {
        label: 'Amount',
        value: removeTrailingZeros(data.amount ?? '0'),
      },
      {
        label: 'Confirmations',
        value: `${data.confirmations ?? '-'} / ${data.target_confirmations ?? '-'}`,
      },
      {
        label: 'Status',
        value: capitalCase(data.status ?? ''),
      },
      {
        label: 'Sweep Status',
        value: capitalCase(data.sweep_status ?? ''),
      },
      {
        label: 'Swap Status',
        value: capitalCase(data.swap_status ?? ''),
      },
      {
        label: 'Provider',
        value: capitalCase(data.provider ?? ''),
      },
      {
        label: 'Name',
        value: data.name,
      },
      {
        label: 'Idempotency',
        value: (data.idempotency_keys && typeof data.idempotency_keys === 'object')
          ? JSON.stringify(data.idempotency_keys)
          : data.idempotency_keys,
        monospace: true
      },
      {
        label: 'Created At',
        value: formatDate(data.created_at),
      },
      {
        label: 'Updated At',
        value: formatDate(data.updated_at),
      }
    ]
  ];

  // --- Render ---
  return (
    <>
      <Dialog
        fullScreen
        open
        onClose={handleClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            bgcolor: 'background.default'
          }
        }}
      >
        <AppBar id="app_bar_height" color='default' sx={{ position: 'relative', boxShadow: 0, borderBottom: `1px solid ${  theme.palette.divider}`, }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">Transaction Details</Typography>
            <IconButton color="error" onClick={handleClose}>
              <GridCloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* LOADING */}
        {!state.loadComplete ? (
          <Container sx={{ mt: 5 }}>
            <Grid container spacing={5}>
              <Grid item>
                <Skeleton sx={{ width: 330 }} height={80} />
                <Skeleton height={80} />
              </Grid>
              <Grid item>
                <Skeleton sx={{ width: 400 }} height={80} />
                <Skeleton height={80} />
                <Skeleton height={80} />
                <Skeleton height={80} />
                <Skeleton sx={{ width: 120 }} height={80} />
                <Skeleton sx={{ width: 120 }} height={80} />
              </Grid>
            </Grid>
          </Container>
        ) : (
          <Box
            sx={{
              mt: { xs: 1, sm: 3 },
              mb: 9, // Leaves space for bottom action bar
              px: { xs: 0.5, sm: 3 }
            }}
          >
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? 'auto' : false}
              sx={{ borderBottom: 1, borderColor: 'divider', background: 'white', position: 'sticky', top: 0, zIndex: 10 }}
            >
              <Tab value={'basic'} label="Basic Info" />
              <Tab value={'payload'} label="Payload" />
              <Tab value={'event_trails'} label="Event Trails" />
            </Tabs>

            {/* BASIC INFO */}
            {currentTab === 'basic' && (
              <Paper
                elevation={3}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  p: { xs: 2, sm: 3 },
                  bgcolor: theme.palette.grey[50],
                  // maxWidth: 810,
                  mx: 'auto'
                }}
              >
                {/* On Hold/Reason */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                  mb={isMobile ? 2 : 3}
                  sx={{ justifyContent: { xs: "flex-start", sm: 'flex-end' } }}
                >
                  <Typography variant="subtitle2">On&nbsp;Hold:</Typography>
                  {data.on_hold ?
                    <Chip
                      label="YES"
                      size="medium"
                      color="error"
                      sx={{ fontWeight: 700, fontSize: '1rem', px: 2, letterSpacing: 1 }}
                    /> :
                    <Chip
                      label="NO"
                      size="medium"
                      color="success"
                      sx={{ fontWeight: 700, fontSize: '1rem', px: 2 }}
                    />
                  }
                  {data.on_hold && !!data.hold_reason && (
                    <Chip
                      label={data.hold_reason}
                      color='warning'
                      variant="filled"
                      size="medium"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1rem',
                        maxWidth: 280,
                        whiteSpace: 'pre-line',
                        ml: 2
                      }}
                    />
                  )}
                </Box>
                <Box
                  component="dl"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    rowGap: 2.2,
                    columnGap: 5,
                    mb: 1
                  }}
                >
                  {/* Primary fields, with copy icon */}
                  {infoGroups[0].map(row =>
                    <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography component="dt" variant="subtitle2" sx={{ minWidth: 110, color: 'text.secondary', pr: 1 }}>{row.label}:</Typography>
                      <Typography component="dd"
                        sx={{
                          fontFamily: row.monospace ? 'Menlo,monospace' : 'inherit',
                          fontWeight: row.label === 'TxID' || row.label === 'Address' ? 700 : 'normal',
                          fontSize: { xs: 13, sm: 15 },
                          whiteSpace: 'nowrap',
                          overflow: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0,
                        }}>
                        {row.value ?? '-'}
                        {row.copy && row.value && (
                          <Tooltip title={`Copy ${row.label}`}>
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() => handleCopy(row.value, row.label)}
                            >
                              <Icon icon="mdi:content-copy" width={18} height={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Typography>
                    </Box>
                  )}

                  {/* Secondary info, no copy */}
                  {infoGroups[1].map(row =>
                    <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography component="dt" variant="subtitle2" sx={{ minWidth: 110, color: 'text.secondary', pr: 1 }}>{row.label}:</Typography>
                      <Typography component="dd"
                        sx={{
                          fontFamily: row.monospace ? 'Menlo,monospace' : 'inherit',
                          fontSize: { xs: 13, sm: 15 },
                          color: row.label === "Status" && String(row.value).toLowerCase() !== "completed"
                            ? 'warning.main' : 'text.primary',
                        }}
                      >
                        {row.value ?? '-'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* PAYLOAD TAB */}
            {currentTab === 'payload' &&
              <JsonPrettyCard value={data.payload} maxHeight={620} />
            }

            {/* EVENT TRAILS TAB */}
            {currentTab === 'event_trails' &&
              <JsonPrettyCard value={data.event_trails} maxHeight={620} />
            }
          </Box>
        )}

        {/* --- Sticky Action Bar --- */}
        <Paper
          elevation={8}
          square
          sx={{
            bgcolor: 'background.paper',
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 1202,
            p: { xs: 1.5, sm: 2 },
            borderTop: `1.5px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-end' }
          }}
        >
          <Button
            color="primary"
            variant="contained"
            disabled={!state.loadComplete}
            size="large"
            startIcon={<Iconify icon="mdi:calculator-variant" />}
            onClick={()=> setModal(true) }
            sx={{ fontWeight: 600, fontSize: 17, minWidth: 190, width: { xs: '100%', sm: 'auto' } }}
          >
            Fix issues
          </Button>
        </Paper>
      </Dialog>
{/* 
        {modal && 
          <TransactionOperationModal
              open={modal}
              txid={txid}
              handleClose={()=>{
                setModal(false);
              }}
              handleComplete={()=>{
                setModal(false);
              }}
          />} */}
    </>
  );
}

// Helper for pretty printing JSON with scroll and fallback if not an object

function JsonPrettyCard({ value, maxHeight }) {
  // Format the JSON
  let pretty = '';
  try {
    pretty = typeof value === 'string'
      ? JSON.stringify(JSON.parse(value), null, 2)
      : JSON.stringify(value, null, 2);
  } catch {
    pretty = typeof value === 'string' ? value : JSON.stringify(value);
  }
  // Split lines for line numbers
  const lines = pretty.split('\n');
  // Copy logic
  const textAreaRef = useRef();

  const handleCopy = () => {
    // fallback for legacy, use navigator if available
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(pretty);
    } else if (textAreaRef.current) {
      textAreaRef.current.select();
      document.execCommand('copy');
    }
  };

  return (
    <Card
      sx={{
        mt: 2,
        borderRadius: 3,
        boxShadow: 1,
        bgcolor: '#191e260a',
        mx: 'auto',
        maxWidth: 950,
        position: 'relative'
      }}
    >
      {/* Copy button in corner */}
      <Box sx={{ position: 'absolute', top: 8, right: 16, zIndex: 2 }}>
        <Tooltip title="Copy JSON">
          <IconButton size="small" color="primary" onClick={handleCopy}>
            <Icon icon="mdi:content-copy" width={18} height={18} />
          </IconButton>
        </Tooltip>
      </Box>
      {/* Hidden textarea for fallback copy */}
      <textarea
        ref={textAreaRef}
        style={{ position: 'absolute', left: -9999, top: -9999 }}
        value={pretty}
        readOnly
        aria-hidden="true"
        tabIndex={-1}
      />
      {/* Main code block */}
      <Scrollbar
        sx={{
          maxHeight: maxHeight || 460,
          minHeight: 180,
          p: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            fontFamily: 'Menlo, Monaco, Consolas, monospace',
            fontSize: 14,
            background: '#f5f7fa',
            px: 0,
            py: 2,
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            boxShadow: 0,
            m: 2,
          }}
        >
          {/* Line numbers */}
          <Box
            sx={{
              textAlign: 'right',
              color: '#b0b4ba',
              userSelect: 'none',
              pr: 2,
              pl: 1,
              borderRight: '1px solid #e0e0e0',
              minWidth: '2.3em',
              pt: 0.3,
            }}
          >
            {lines.map((_, i) => (
              <div key={i} style={{ height: '1.3em' }}>{i + 1}</div>
            ))}
          </Box>
          {/* Code content */}
          <pre
            style={{
              margin: 0,
              padding: 0,
              fontFamily: 'inherit',
              color: '#22262a',
              flex: 1,
              background: 'transparent',
              lineHeight: 1.55,
              overflow: 'auto',
              whiteSpace: 'pre',
            }}
            // tabIndex={0}
          >
            {pretty || <i style={{ color: '#888' }}>No Data</i>}
          </pre>
        </Box>
      </Scrollbar>
    </Card>
  );
}
